if (!customElements.get('sonee-store-stock')) {
  customElements.define('sonee-store-stock', class extends HTMLElement {
    connectedCallback() {
      this.productSection = this.closest('.product-section');
      this.output = this.querySelector('[data-store-stock-output]');
      this.onVariantChange = event => this.load(event.detail.variant?.id);
      this.productSection.addEventListener('variantChange', this.onVariantChange);
      this.onMapClick = event => {
        const open = event.target.closest('[data-store-map-open]');
        if (open && this.contains(open)) {
          this.openMap(open.closest('[data-place-id]'));
          return;
        }
        const close = event.target.closest('[data-store-map-close]');
        if (close && this.contains(close)) close.closest('dialog').close();
      };
      this.addEventListener('click', this.onMapClick);
      this.load(this.dataset.variantId);
      this.hoursTimer = setInterval(() => {
        this.updateHours();
        this.updateGoogleCountdowns();
      }, 60000);
      this.onVisibility = () => { if (!document.hidden) this.updateGoogleCountdowns(); };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.productSection.removeEventListener('variantChange', this.onVariantChange);
      this.removeEventListener('click', this.onMapClick);
      this.controller?.abort();
      clearInterval(this.hoursTimer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.googlePlaces = null;
    }

    // opening_hours is a JSON metafield: mon..sun -> [["09:00", "22:00"]].
    // Multiple intervals support midday breaks; an empty array means closed.
    updateHours(now = new Date()) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Indian/Maldives', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(now);
      const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
      const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const day = values.weekday.toLowerCase();
      const minutes = Number(values.hour) * 60 + Number(values.minute);
      const parseTime = value => {
        if (typeof value !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return null;
        const [hour, minute] = value.split(':').map(Number);
        return hour * 60 + minute;
      };
      for (const label of this.querySelectorAll('[data-store-hours]')) {
        label.hidden = true;
        try {
          const schedule = JSON.parse(label.dataset.storeHours);
          const today = schedule[day];
          if (!Array.isArray(today)) continue;
          const previous = schedule[days[(days.indexOf(day) + 6) % 7]] || [];
          let closes;
          for (const interval of [...previous.map(pair => ({ pair, yesterday: true })), ...today.map(pair => ({ pair, yesterday: false }))]) {
            if (!Array.isArray(interval.pair) || interval.pair.length !== 2) continue;
            const [start, end] = interval.pair.map(parseTime);
            if (start === null || end === null || start === end) continue;
            const isOpen = interval.yesterday
              ? end < start && minutes < end
              : minutes >= start && (end < start || minutes < end);
            if (isOpen && (closes === undefined || end < closes)) closes = end;
          }
          if (closes === undefined) {
            label.textContent = 'Closed now';
          } else {
            const hour = Math.floor(closes / 60);
            const minute = closes % 60;
            label.textContent = `Closes at ${hour % 12 || 12}${minute ? `:${String(minute).padStart(2, '0')}` : ''}${hour < 12 ? 'am' : 'pm'}`;
          }
          label.hidden = false;
        } catch { /* Unconfigured or invalid hours must not become customer-facing claims. */ }
      }
    }

    updateGoogleCountdowns() {
      if (!this.googlePlaces) return;
      // Never keep displaying hours across a date change or an idle tab indefinitely.
      if (Date.now() - this.googleFetchedAt >= 15 * 60 * 1000) {
        this.querySelectorAll('[data-google-hours]').forEach(el => { el.hidden = true; });
        if (!document.hidden) this.refreshGoogleHours();
        return;
      }
      for (const [row, place] of this.googlePlaces) {
        if (!row.isConnected) continue;
        const result = window.soneeGoogleHours.countdown(place);
        const output = row.querySelector('[data-google-hours]');
        output.hidden = !result;
        if (!result) continue;
        const label = row.querySelector('[data-google-countdown]');
        label.textContent = result.text;
        label.title = result.text;
      }
    }

    openMap(row) {
      const dialog = row?.querySelector('[data-store-map-dialog]');
      if (!dialog) return;
      dialog.showModal();
      const mapElement = dialog.querySelector('[data-store-map]');
      if (!mapElement || mapElement.dataset.loaded) return;
      const key = this.querySelector('[data-store-stock-content]')?.dataset.googlePlacesKey;
      if (!key || !row.dataset.placeId) {
        mapElement.classList.add('sonee-store-stock__map--unavailable');
        mapElement.textContent = 'Map unavailable here. Open this location in Google Maps.';
        return;
      }
      const iframe = document.createElement('iframe');
      iframe.title = `Map of ${row.querySelector('.sonee-store-stock__name')?.textContent || 'store'}`;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.allowFullscreen = true;
      iframe.src = `https://www.google.com/maps/embed/v1/place?${new URLSearchParams({key, q: `place_id:${row.dataset.placeId}`, zoom: '16'})}`;
      mapElement.replaceChildren(iframe);
      mapElement.dataset.loaded = 'true';
    }

    async refreshGoogleHours() {
      if (this.googleLoading || !window.soneeGoogleHours || !this.isConnected) return;
      const content = this.querySelector('[data-store-stock-content]');
      const key = content?.dataset.googlePlacesKey;
      if (!key) return;
      const rows = [...content.querySelectorAll('[data-place-id]')].filter(row => row.dataset.placeId);
      if (!rows.length) return;
      this.googleLoading = true;
      const results = await Promise.all(rows.map(async row => {
        try {
          const place = await window.soneeGoogleHours.fetchPlace(key, row.dataset.placeId);
          if (!row.isConnected || content !== this.querySelector('[data-store-stock-content]')) return null;
          const weekly = row.querySelector('[data-google-weekly-hours]');
          weekly.replaceChildren();
          for (const day of place.currentOpeningHours?.weekdayDescriptions || []) {
            const line = document.createElement('p');
            const separator = day.indexOf(':');
            if (separator > 0) {
              const name = document.createElement('span');
              const hours = document.createElement('span');
              name.textContent = day.slice(0, separator);
              hours.textContent = day.slice(separator + 1).trim();
              line.append(name, hours);
            } else {
              line.textContent = day;
            }
            weekly.appendChild(line);
          }
          const providers = row.querySelector('[data-google-providers]');
          providers.replaceChildren();
          for (const attribution of place.attributions || []) {
            const text = document.createElement('span'); text.textContent = attribution.provider || ''; providers.appendChild(text);
          }
          return [row, place];
        } catch {
          if (row.isConnected) {
            row.querySelector('[data-google-hours]').hidden = true;
            row.querySelector('[data-google-weekly-hours]').replaceChildren();
            row.querySelector('[data-google-providers]').replaceChildren();
          }
          return null;
        }
      }));
      this.googleLoading = false;
      if (content !== this.querySelector('[data-store-stock-content]')) {
        this.refreshGoogleHours();
        return;
      }
      this.googlePlaces = results.filter(Boolean);
      this.googleFetchedAt = Date.now();
      this.updateGoogleCountdowns();
    }

    async load(variantId) {
      this.controller?.abort();
      this.googlePlaces = null;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.output.textContent = variantId ? 'Checking store availability…' : 'Select an available combination to check stores.';
      this.output.setAttribute('aria-busy', String(Boolean(variantId)));
      if (!variantId) return;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}variants/${encodeURIComponent(variantId)}/?section_id=product-store-stock`, { signal });
        if (!response.ok) throw new Error('Store availability request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-store-stock-content]');
        if (!content) throw new Error('Store availability missing');
        if (!signal.aborted) {
          this.output.replaceChildren(content);
          this.updateHours();
          this.refreshGoogleHours();
        }
      } catch (error) {
        if (!signal.aborted) this.output.textContent = 'Store availability could not be loaded. Please contact us to check.';
      } finally {
        if (!signal.aborted) this.output.setAttribute('aria-busy', 'false');
      }
    }
  });
}
